import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose, DialogDescription } from './ui/dialog';
import { Attachment } from '../types/index';
import { toast } from 'sonner';
import { 
  Upload, 
  File, 
  FileText, 
  Image, 
  Video, 
  Trash2, 
  Download,
  AlertCircle,
  X,
  Edit,
  Check,
  X as XIcon,
  Eye
} from 'lucide-react';
import { cn } from './ui/utils';
import FilePreview from './FilePreview';
import { Can } from "../components/Can";
import { ACTIONS, RESOURCES } from "../constants/permissions.constants";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

interface FileAttachmentsProps {
  resourceType: 'trainingplans' | 'activities' | 'users' | 'jobroles' | 'procedures' | 'policies' | 'safety-incidents';
  // Optional: when omitted, files are staged locally (deferred upload) instead of
  // being uploaded immediately — the parent uploads them once the resource exists.
  resourceId?: string;
  attachments: Attachment[];
  onAttachmentsChange: (attachments: Attachment[]) => void;
  // canEdit: boolean;
  disabled?: boolean;
  // Optional partition for backends storing multiple attachment lists per resource
  // (sent as ?category= on upload/delete/rename).
  category?: string;
  // Card title — defaults to "Attachments".
  title?: string;
}

// resourceType as exposed to callers → API path segment(s).
const getResourcePath = (resourceType: FileAttachmentsProps['resourceType']): string =>
  resourceType === 'safety-incidents' ? 'safety/incidents' : resourceType;

const getAttachmentSubject = (resourceType: FileAttachmentsProps['resourceType']): string => {
  switch (resourceType) {
    case 'users':
      return RESOURCES.PERSON_ATTACHMENTS;
    case 'jobroles':
      return RESOURCES.JOB_ROLE_ATTACHMENTS;
    case 'procedures':
      return RESOURCES.PROCEDURE_ATTACHMENTS;
    case 'activities':
      return RESOURCES.ACTIVITY_ATTACHMENTS;
    case 'trainingplans':
      return RESOURCES.TRAINING_PLAN;
    case 'policies':
      return RESOURCES.POLICY_ATTACHMENT;
    case 'safety-incidents':
      return RESOURCES.INCIDENT_INJURY;
    default:
      // Return a default or throw an error if the mapping is not found
      console.error(`No attachment resource mapping found for resourceType: ${resourceType}`);
      return 'attachment'; // A generic fallback
  }
};

// Helper function to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Helper function to get file icon based on MIME type
const getFileIcon = (mimetype: string, isPreviewableFile: boolean) => {
  const iconClasses = 'w-4 h-4 text-gray-500';
  
  if (mimetype.startsWith('image/')) {
    return <Image className={iconClasses} />;
  } else if (mimetype.startsWith('video/')) {
    return <Video className={iconClasses} />;
  } else if (mimetype.includes('pdf')) {
    return <FileText className={iconClasses} />;
  } else if (mimetype.includes('word') || mimetype.includes('document')) {
    return <FileText className={iconClasses} />;
  } else if (mimetype.includes('excel') || mimetype.includes('spreadsheet')) {
    return <FileText className={iconClasses} />;
  } else {
    return <File className={iconClasses} />;
  }
};

// Helper function to get clean file type display
const getFileTypeDisplay = (mimetype: string): string => {
  const typeMap: { [key: string]: string } = {
    'application/pdf': 'PDF',
    'application/msword': 'DOC',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
    'application/vnd.ms-excel': 'XLS',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
    'image/jpeg': 'JPG',
    'image/jpg': 'JPG',
    'image/png': 'PNG',
    'image/gif': 'GIF',
    'image/webp': 'WebP',
    'video/mp4': 'MP4',
    'video/webm': 'WebM',
    'video/ogg': 'OGG',
    'text/plain': 'TXT',
  };

  return typeMap[mimetype] || mimetype.split('/')[1]?.toUpperCase() || 'FILE';
};

// Helper function to get file type badge color
const getFileTypeBadge = (mimetype: string) => {
  if (mimetype.startsWith('image/')) {
    return 'bg-purple-100 text-purple-800';
  } else if (mimetype.startsWith('video/')) {
    return 'bg-orange-100 text-orange-800';
  } else if (mimetype.includes('pdf')) {
    return 'bg-red-100 text-red-800';
  } else if (mimetype.includes('excel') || mimetype.includes('spreadsheet')) {
    return 'bg-green-100 text-green-800';
  } else if (mimetype.includes('word') || (mimetype.includes('document') && mimetype.includes('wordprocessing'))) {
    return 'bg-blue-100 text-blue-800';
  } else {
    return 'bg-gray-100 text-gray-800';
  }
};

export default function FileAttachments({ 
  resourceType,
  resourceId,
  attachments, 
  onAttachmentsChange, 
  // canEdit,
  disabled = false,
  category,
  title = 'Attachments'
}: FileAttachmentsProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [newFilename, setNewFilename] = useState('');
  const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState<Attachment | null>(null);

  const attachmentSubject = getAttachmentSubject(resourceType);
  const categoryQuery = category ? `?category=${encodeURIComponent(category)}` : '';
  
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (disabled) {
      toast.error("You don't have permission to upload files");
      return;
    }

    // Additional client-side validation as backup
    const validFiles: File[] = [];
    const maxSize = 200 * 1024 * 1024; // 200MB
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'video/mp4'];
    const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const allAllowedTypes = [...allowedTypes, ...allowedImageTypes];

    acceptedFiles.forEach(file => {
      const isValidSize = file.size <= maxSize;
      const isValidType = allAllowedTypes.includes(file.type) || file.type.startsWith('image/');

      // Prioritize format errors over size errors
      if (!isValidType) {
        toast.error(`${file.name}: File format not supported. Supported formats: PDF, DOC, DOCX, XLS, XLSX, Images, MP4.`);
        return;
      }

      if (!isValidSize) {
        toast.error(`${file.name}: File size too large. Maximum size is 200MB.`);
        return;
      }

      validFiles.push(file);
    });

    if (validFiles.length === 0) {
      return; // No valid files to upload
    }

    // Staged mode (no resourceId yet): keep files locally; the parent uploads
    // them after the resource is created.
    if (!resourceId) {
      const staged: Attachment[] = validFiles.map((file) => ({
        id: `staged_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        filename: file.name,
        key: '',
        url: '',
        size: file.size,
        mimetype: file.type,
        uploadedBy: '',
        uploadedAt: new Date().toISOString(),
        file,
      }));
      onAttachmentsChange([...attachments, ...staged]);
      toast.success(`${staged.length} file(s) added`);
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadingFiles(validFiles.map(f => f.name));

    try {
      const formData = new FormData();
      validFiles.forEach(file => {
        formData.append('files', file);
      });

      const response = await fetch(`${API_BASE}/api/${getResourcePath(resourceType)}/${resourceId}/attachments${categoryQuery}`, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const result = await response.json();
      
      if (result.success) {
        // Update attachments list
        const newAttachments = [...attachments, ...result.attachments];
        onAttachmentsChange(newAttachments);
        
        if (result.attachments.length > 0) {
          toast.success(`${result.attachments.length} file(s) uploaded successfully`);
        }
        
        // Show backend validation errors if any
        if (result.errors && result.errors.length > 0) {
          result.errors.forEach((error: any) => {
            if (error.errors && Array.isArray(error.errors)) {
              // Check for format and size errors in the array
              const hasFormatError = error.errors.some((err: string) => 
                err.includes('Invalid file type') || err.includes('not allowed')
              );
              const hasSizeError = error.errors.some((err: string) => 
                err.includes('File too large')
              );
              
              if (hasFormatError) {
                // Prioritize format error
                toast.error(`${error.filename}: File format not supported. Supported formats: PDF, DOC, DOCX, XLS, XLSX, Images, MP4.`);
              } else if (hasSizeError) {
                // Show size error only if no format error
                toast.error(`${error.filename}: File size too large. Maximum size is 200MB.`);
              } else {
                // Show other errors
                error.errors.forEach((err: string) => {
                  toast.error(`${error.filename}: ${err}`);
                });
              }
            } else {
              const errorMsg = error.error || error.message || 'Upload failed';
              if (errorMsg.includes('Invalid file type') || errorMsg.includes('not allowed')) {
                toast.error(`${error.filename}: File format not supported. Supported formats: PDF, DOC, DOCX, XLS, XLSX, Images, MP4.`);
              } else if (errorMsg.includes('File too large')) {
                toast.error(`${error.filename}: File size too large. Maximum size is 200MB.`);
              } else {
                toast.error(`${error.filename}: ${errorMsg}`);
              }
            }
          });
        }
      } else {
        if (result.message && result.message.includes('No files uploaded')) {
          toast.error('No valid files to upload. Please check file size (max 200MB) and format.');
        } else {
          toast.error(result.message || 'Upload failed');
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadingFiles([]);
    }
  }, [resourceType, resourceId, attachments, onAttachmentsChange, disabled, categoryQuery]);

  const handleDeleteClick = (attachment: Attachment) => {
    if (disabled) {
      toast.error("You don't have permission to delete files");
      return;
    }
    setAttachmentToDelete(attachment);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!attachmentToDelete) return;

    // Staged mode: nothing exists on the server yet — just remove locally.
    if (!resourceId) {
      onAttachmentsChange(attachments.filter(att => att.id !== attachmentToDelete.id));
      toast.success('Attachment removed');
      setDeleteDialogOpen(false);
      setAttachmentToDelete(null);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/${getResourcePath(resourceType)}/${resourceId}/attachments/${attachmentToDelete.id}${categoryQuery}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const result = await response.json();

      if (result.success) {
        // Remove from attachments list
        const updatedAttachments = attachments.filter(att => att.id !== attachmentToDelete.id);
        onAttachmentsChange(updatedAttachments);
        toast.success('Attachment deleted successfully');
      } else {
        toast.error(result.message || 'Delete failed');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Delete failed. Please try again.');
    } finally {
      setDeleteDialogOpen(false);
      setAttachmentToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAttachmentToDelete(null);
  };

  const handleDownload = async (attachment: Attachment) => {
    // Staged files have no server copy yet — download straight from the File object.
    if (attachment.file) {
      const objectUrl = URL.createObjectURL(attachment.file);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = attachment.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
      return;
    }

    try {
      // Generate a fresh signed URL for download
      const response = await fetch(`${API_BASE}/api/${getResourcePath(resourceType)}/${resourceId}/attachments/${attachment.id}/download`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const result = await response.json();

      if (result.success && result.downloadUrl) {
        // Create a temporary link to trigger download
        const link = document.createElement('a');
        link.href = result.downloadUrl;
        link.download = attachment.filename;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        toast.error('Failed to generate download link. Please try again.');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Download failed. Please try again.');
    }
  };

  const handleStartRename = (attachment: Attachment) => {
    setRenamingId(attachment.id);
    // Remove extension for editing, we'll add it back automatically
    const filenameWithoutExt = attachment.filename.replace(/\.[^/.]+$/, '');
    setNewFilename(filenameWithoutExt);
  };

  const handleRename = async (attachmentId: string) => {
    if (!newFilename.trim()) {
      toast.error('Please enter a valid filename');
      return;
    }

    // Staged mode: rename locally (original extension preserved).
    if (!resourceId) {
      const target = attachments.find(att => att.id == attachmentId);
      const ext = target?.filename.match(/\.[^/.]+$/)?.[0] || '';
      const updatedAttachments = attachments.map(att =>
        att.id == attachmentId ? { ...att, filename: newFilename.trim() + ext } : att
      );
      onAttachmentsChange(updatedAttachments);
      toast.success('File renamed successfully');
      setRenamingId(null);
      setNewFilename('');
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/${getResourcePath(resourceType)}/${resourceId}/attachments/${attachmentId}/rename${categoryQuery}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ newFilename: newFilename.trim() }),
      });

      const result = await response.json();

      if (result.success) {
        // Update the attachment in the local state
        const updatedAttachments = attachments.map(att => 
          att.id == attachmentId ? result.attachment : att
        );
        onAttachmentsChange(updatedAttachments);
        toast.success('File renamed successfully');
        setRenamingId(null);
        setNewFilename('');
      } else {
        toast.error(result.message || 'Failed to rename file');
      }
    } catch (error) {
      console.error('Rename error:', error);
      toast.error('Failed to rename file. Please try again.');
    }
  };

  const handleCancelRename = () => {
    setRenamingId(null);
    setNewFilename('');
  };

  // Check if file is previewable
  const isPreviewable = (mimetype: string) => {
    const previewableTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
      'application/pdf',
      'video/mp4', 'video/webm', 'video/ogg',
      'text/plain', 'text/html', 'text/css', 'text/javascript',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    ];
    return previewableTypes.includes(mimetype);
  };

  const handlePreview = (attachment: Attachment) => {
    // Staged files have no server copy yet — open the local object directly.
    if (attachment.file) {
      const objectUrl = URL.createObjectURL(attachment.file);
      window.open(objectUrl, '_blank');
      return;
    }
    setPreviewAttachment(attachment);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewAttachment(null);
  };

  // Handle clicking on the attachment (preview if available, otherwise download)
  const handleAttachmentClick = (attachment: Attachment) => {
    if (renamingId === attachment.id) {
      // Don't trigger click when renaming
      return;
    }
    
    if (isPreviewable(attachment.mimetype)) {
      handlePreview(attachment);
    } else {
      handleDownload(attachment);
    }
  };

  // Handle rejected files with specific error messages
  const onDropRejected = useCallback((rejectedFiles: Array<any>) => {
    rejectedFiles.forEach((rejectedFile) => {
      const { file, errors } = rejectedFile;
      
      // Prioritize file format errors over size errors
      const hasFormatError = errors.some((e: any) => e.code === 'file-invalid-type');
      const hasSizeError = errors.some((e: any) => e.code === 'file-too-large');
      const hasTooManyError = errors.some((e: any) => e.code === 'too-many-files');
      
      if (hasFormatError) {
        // Only show format error if it exists
        toast.error(`${file.name}: File format not supported. Supported formats: PDF, DOC, DOCX, XLS, XLSX, Images, MP4.`);
      } else if (hasSizeError) {
        // Show size error only if no format error
        toast.error(`${file.name}: File size too large. Maximum size is 200MB.`);
      } else if (hasTooManyError) {
        toast.error('Too many files selected. Please select files one at a time or in smaller batches.');
      } else {
        // Show other errors
        errors.forEach((error: any) => {
          toast.error(`${file.name}: ${error.message || 'File validation failed'}`);
        });
      }
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    disabled: disabled || uploading,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
      'video/mp4': ['.mp4']
    },
    maxSize: 200 * 1024 * 1024, // 200MB
    multiple: true
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="w-5 h-5" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Upload Area */}
        <Can I={ACTIONS.UPDATE} a={attachmentSubject}>
          {!disabled && (
            <div
              {...getRootProps()}
              className={cn(
                "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
                isDragActive 
                  ? "border-blue-500 bg-blue-50" 
                  : "border-gray-300 hover:border-gray-400",
                uploading && "pointer-events-none opacity-50"
              )}
            >
              <input {...getInputProps()} />
              <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
              {uploading ? (
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">Uploading files...</p>
                  <div className="space-y-1">
                    {uploadingFiles.map((filename, index) => (
                      <p key={index} className="text-xs text-gray-500">{filename}</p>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-600 mb-1">
                    {isDragActive 
                      ? "Drop files here..." 
                      : "Drag & drop files here, or click to select"
                    }
                  </p>
                  <p className="text-xs text-gray-500">
                    Supports: PDF, DOC, DOCX, XLS, XLSX, Images, MP4 (max 200MB each)
                  </p>
                </div>
              )}
            </div>
          )}
        </Can>

        {/* Attachments List */}
        {attachments.length > 0 ? (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700">Files ({attachments.length})</h4>
            <div className="space-y-2">
              {attachments.map((attachment, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                  onClick={() => handleAttachmentClick(attachment)}
                  title={isPreviewable(attachment.mimetype) ? `Click to preview ${attachment.filename}` : `Click to download ${attachment.filename}`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {getFileIcon(attachment.mimetype, isPreviewable(attachment.mimetype))}
                    <div className="flex-1 min-w-0">
                      {renamingId === attachment.id ? (
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <Input
                            value={newFilename}
                            onChange={(e) => setNewFilename(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleRename(attachment.id);
                              } else if (e.key === 'Escape') {
                                handleCancelRename();
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="text-sm h-7"
                            autoFocus
                            placeholder="Enter new filename"
                          />
                          <span className="text-xs text-gray-500">
                            {attachment.filename.match(/\.[^/.]+$/)?.[0] || ''}
                          </span>
                        </div>
                      ) : (
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {attachment.filename}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <Badge 
                          variant="secondary" 
                          className={cn("text-xs", getFileTypeBadge(attachment.mimetype))}
                        >
                          {getFileTypeDisplay(attachment.mimetype)}
                        </Badge>

                        <span className="text-xs text-gray-500">
                          {formatFileSize(attachment.size)}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(attachment.uploadedAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    {renamingId === attachment.id ? (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRename(attachment.id);
                          }}
                          className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCancelRename();
                          }}
                          className="h-8 w-8 p-0 text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                        >
                          <XIcon className="w-4 h-4" />
                        </Button>
                      </>
                    ) : (
                      <>
                        {isPreviewable(attachment.mimetype) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePreview(attachment);
                            }}
                            className="h-8 w-8 p-0 text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                            title="Preview file"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(attachment);
                          }}
                          className="h-8 w-8 p-0 text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                          title="Download file"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        {!disabled && (
                          <>
                            <Can I={ACTIONS.UPDATE} a={attachmentSubject}>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStartRename(attachment);
                                }}
                                className="h-8 w-8 p-0 text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                                title="Rename file"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            </Can>

                            <Can I={ACTIONS.UPDATE} a={attachmentSubject}>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(attachment);
                                }}
                                className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                title="Delete file"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </Can>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <File className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p className="text-sm text-gray-500">No attachments yet</p>
            <Can I={ACTIONS.UPDATE} a={attachmentSubject}>
              {!disabled && (
                <p className="text-xs text-gray-400 mt-1">
                  Upload files to get started
                </p>
              )}
            </Can>
          </div>
        )}
      </CardContent>
      
      {/* File Preview Modal */}
      {previewAttachment && resourceId && (
        <FilePreview
          resourceType={resourceType}
          resourceId={resourceId}
          attachment={previewAttachment}
          isOpen={previewOpen}
          onClose={handleClosePreview}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Attachment</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this attachment?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600">
              <strong>{attachmentToDelete?.filename}</strong>
            </p>
            <p className="text-sm text-gray-500 mt-1">
              This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" onClick={handleDeleteCancel}>
                Cancel
              </Button>
            </DialogClose>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
} 